import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { UtensilsCrossed, Map, ChevronRight, ShoppingBasket, BookOpen } from "lucide-react";
import { api } from "@/lib/api";

export default function PackHub() {
  const { id } = useParams();
  const [addon, setAddon] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    api.get(`/addons/${id}`)
      .then((r) => { setAddon(r.data); setStatus("ok"); })
      .catch(() => setStatus("error"));
  }, [id]);

  if (status === "loading") {
    return <div className="min-h-screen flex items-center justify-center bg-brand-bg text-brand-forest/60">Loading…</div>;
  }

  const hasList = !!addon?.info_content?.trim();
  const hasMap = (addon?.map_spots || []).some((s) => typeof s.latitude === "number");
  const guidePages = addon?.guide_pages || [];

  if (status === "error" || (!hasList && !hasMap && guidePages.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg text-center px-6" data-testid="pack-hub-empty">
        <div>
          <UtensilsCrossed size={40} className="mx-auto text-brand-terracotta mb-4" />
          <p className="font-display text-2xl text-brand-forest">Nothing here yet</p>
          <p className="text-brand-forest/60 mt-2">This guide isn't available. Please check with your host.</p>
        </div>
      </div>
    );
  }

  const tiles = [
    ...guidePages.map((p, i) => ({ to: `/guide/${id}/${i}`, icon: BookOpen, testid: `hub-tile-guide-${i}`, title: p.title, desc: p.subtitle || "Tap to open this guide." })),
    hasList && { to: `/pack/${id}`, icon: UtensilsCrossed, testid: "hub-tile-list", title: "Food Ideas List", desc: "100+ grab-and-go snacks, sandwiches, fruit, sweets & drinks." },
    hasMap && { to: `/picnic-map/${id}`, icon: Map, testid: "hub-tile-map", title: "Picnic Spots Map", desc: "Top picnic spots around Apache Junction, with directions." },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="pack-hub-page">
      <div className="bg-brand-forest text-white">
        <div className="max-w-2xl mx-auto px-6 py-14 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-brand-terracotta/20 flex items-center justify-center mb-5">
            <ShoppingBasket size={28} className="text-brand-terracotta" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight" data-testid="pack-hub-title">{addon?.title || "Adventure Pack"}</h1>
          <p className="text-white/70 mt-3">Pick a guide below to get started.</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-4">
        {tiles.map((t, i) => (
          <motion.div key={t.testid} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.08 }}>
            <Link
              to={t.to}
              data-testid={t.testid}
              className="group flex items-center gap-4 bg-white rounded-3xl p-6 border border-black/5 shadow-sm hover:border-brand-terracotta/40 active:scale-[0.99] transition-all"
            >
              <span className="h-14 w-14 shrink-0 rounded-2xl bg-brand-sand flex items-center justify-center">
                <t.icon size={26} className="text-brand-terracotta" />
              </span>
              <div className="flex-1">
                <h2 className="font-display text-xl sm:text-2xl tracking-tight">{t.title}</h2>
                <p className="text-sm text-brand-forest/60 mt-1 leading-relaxed">{t.desc}</p>
              </div>
              <ChevronRight size={22} className="text-brand-forest/30 group-hover:text-brand-terracotta group-hover:translate-x-1 transition-all shrink-0" />
            </Link>
          </motion.div>
        ))}
        <p className="text-center text-xs text-brand-forest/40 pt-4">MyAZbnb.com</p>
      </div>
    </div>
  );
}
